import React from 'react';
import './MOQWarningCard.scss';
import { Card } from '@orderly/morrisons-component-library';
import { IMOQValidationResponse } from '../../api/MOQ/IMOQValidationResponse';
import { formatUTCDateShort } from '../../helpers/Format';

interface IMOQWarningCardProps {
  moqValidation?: IMOQValidationResponse;
  deliveryDate?: string;
  combinedOrder: boolean;
}

const MOQWarningCard: React.FC<IMOQWarningCardProps> = ({
  moqValidation,
  combinedOrder,
}) => {
  if (!moqValidation || !moqValidation.isMoqApplicable || moqValidation.overallStatus === 'Success') {
    return null;
  }

  const renderCategoryWarnings = () => {
    if (!moqValidation.categories || moqValidation.categories.length === 0) {
      return null;
    }

    const failedCategories = moqValidation.categories.filter(cat => cat.status === 'Failed');

    if (failedCategories.length === 0) {
      return null;
    }

    return (
      <div className="moq-category-warnings">
        {failedCategories.map((category, index) => {
          const deliveryDateToUse = category.alternativeDeliveryDate || category.deliveryDate
            || moqValidation.alternativeDeliveryDate || moqValidation.deliveryDate;
          const categoryDeliveryDate = deliveryDateToUse
            ? formatUTCDateShort(new Date(deliveryDateToUse))
            : null;

          return (
            <div key={index} className="moq-warning-item category-warning">
              <span className="warning-icon">⚠️</span>
              <span className="warning-text">
                Your <strong>{category.categoryCode}</strong> order{' '}
                {categoryDeliveryDate && (
                  <>for delivery on <strong>{categoryDeliveryDate}</strong> </>
                )}
                is currently <strong>{category.current}</strong> cases which is below Minimum Order Quantity of{' '}
                <strong>{category.required}</strong> cases
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayWarning = () => {
    if (!moqValidation.day || moqValidation.day.status === 'Success') {
      return null;
    }

    // Prioritize alternative delivery date
    const deliveryDateToUse = moqValidation.alternativeDeliveryDate || moqValidation.deliveryDate;
    const formattedDeliveryDate = deliveryDateToUse
      ? formatUTCDateShort(new Date(deliveryDateToUse))
      : null;

    return (
      <div className="moq-day-warning">
        <div className="moq-warning-item day-warning">
          <span className="warning-icon">⚠️</span>
          <span className="warning-text">
            Your order{combinedOrder ? 's' : ''}{' '}
            {formattedDeliveryDate && (
              <>for delivery on <strong>{formattedDeliveryDate}</strong> </>
            )}
            {combinedOrder ? 'are' : 'is'} currently{' '}
            <strong>{moqValidation.day.current}</strong> cases which is below Minimum Order Quantity of{' '}
            <strong>{moqValidation.day.required}</strong> cases
          </span>
        </div>
      </div>
    );
  };

  const renderActionWarning = () => {
    if (moqValidation.actionOnBreach === 'Reject') {
      return (
        <div className="moq-action-warning reject">
          <span className="action-icon">🚫</span>
          <span className="action-text">
            <strong>Checkout is blocked</strong> - Orders will be rejected if MOQ requirements are not met
          </span>
        </div>
      );
    }

    if (moqValidation.actionOnBreach === 'Penalty' && moqValidation.penaltyAmount > 0) {
      return (
        <div className="moq-action-warning penalty">
          <span className="action-icon">💰</span>
          <span className="action-text">
            A penalty charge of <strong>£{moqValidation.penaltyAmount.toFixed(2)}</strong> will be applied if MOQ
            requirements are not met
          </span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="moq-warning-card-wrapper">
      <Card className="moq-warning-card">
        <div className="moq-warning-header">
          <h4>Minimum Order Quantity Warning</h4>
        </div>

        <div className="moq-warning-content">
          {renderCategoryWarnings()}
          {renderDayWarning()}
          {renderActionWarning()}
        </div>
      </Card>
    </div>
  );
};

export default MOQWarningCard;
